import _isNil from 'lodash/isNil';
import _isEqual from 'lodash/isEqual';
import ValueMappers, { SelectOption } from '../ValueMappers';

// note that codings are not supported for open input.  This should be restricted at the config level
export type SupportedTypes = number | string | fhir.Coding;
export type SupportedArrayTypes = number[] | string[] | fhir.Coding[];
export type RawTypes = SupportedTypes | SupportedArrayTypes;
export type SupportedItemType =
  | 'integer'
  | 'string'
  | 'coding'
  | 'date'
  | 'time';

/**
 * Convert form value array to an options list
 */
export const formValuesToSelectOptions = (
  formValues: SupportedArrayTypes,
  itemType: SupportedItemType,
): SelectOption[] => {
  if (itemType === 'integer') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.integersToSelectOptions(formValues as number[]);
  } else if (itemType === 'coding') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.codingsToSelectOptions(formValues as fhir.Coding[]);
  } else if (itemType === 'string') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.stringsToSelectOptions(formValues as string[]);
  } else if (itemType === 'date') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.dateToSelectOptions(formValues as fhir.date[]);
  } else if (itemType === 'time') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.timeToSelectOptions(formValues as fhir.time[]);
  } else {
    console.error(
      `Select received an uknown type of "${itemType}" and cannot generate options for it`,
    );
    return [];
  }
};

/**
 * Convert form value to a select option
 */
export const formValueToSelectOption = (
  formValue: SupportedTypes,
  itemType: SupportedTypes,
): SelectOption => {
  if (itemType === 'integer') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.integerToSelectOption(formValue as number);
  } else if (itemType === 'coding') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.codingToSelectOption(formValue as fhir.Coding);
  } else if (itemType === 'string') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.stringToSelectOption(formValue as string);
  } else if (itemType === 'date') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.dateToSelectOption(formValue as fhir.date);
  } else if (itemType === 'time') {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return ValueMappers.timeToSelectOption(formValue as fhir.time);
  } else {
    console.error(
      `Select received an uknown type of "${itemType}" and cannot generate options for it`,
    );
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return null;
  }
};

export type Config = {
  isMultiSelect: boolean;
  isOpenChoice: boolean;
  itemType: SupportedItemType;
};

export default class {
  private _config: Config;
  private _configuredOptions: SupportedArrayTypes;
  private _values: SupportedArrayTypes;

  constructor(
    config: Config,
    configuredOptions: SupportedArrayTypes,
    value: RawTypes,
  ) {
    this._config = config;
    this._configuredOptions = configuredOptions;
    this._values = Array.isArray(value)
      ? value
      : ([value] as SupportedArrayTypes);
  }

  private get _openChoiceValue(): SupportedArrayTypes {
    return (this._values as SupportedTypes[]).filter(
      value =>
        !(this._configuredOptions as SupportedTypes[]).find(configuredOption =>
          _isEqual(configuredOption, value),
        ),
    ) as SupportedArrayTypes;
  }

  get openChoiceSelectOptions(): SelectOption[] {
    return this._config.isOpenChoice
      ? formValuesToSelectOptions(this._openChoiceValue, this._config.itemType)
      : [];
  }

  get configuredSelectOptions(): SelectOption[] {
    return formValuesToSelectOptions(
      this._configuredOptions,
      this._config.itemType,
    );
  }

  get allSelectOptions(): SelectOption[] {
    return [...this.openChoiceSelectOptions, ...this.configuredSelectOptions];
  }

  /**
   * Takes the onChange value from a select and gets the form value
   */
  public processSingleSelectChange(value: string): SupportedTypes {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (_isNil(value) || value === '') return null;

    const targetSelectOption = this.allSelectOptions.find(
      _ => _.displayValue.value === value,
    );

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!targetSelectOption) return null;

    // if selecting the already selected item, treat it as "toggling off"
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return _isEqual(targetSelectOption.formValue, this._values?.[0])
      ? null
      : targetSelectOption.formValue;
  }

  /**
   * The value of the underlying select field
   */
  get selectValue(): string {
    if (_isNil(this._values) || _isNil(this._values?.[0])) return '';

    // this will always be an array of 1, it's just normalized across multiselect for convenience
    const formValue = this._values?.[0];
    const selectValue = this.allSelectOptions.find(_ =>
      _isEqual(_.formValue, formValue),
    );
    return selectValue?.displayValue?.value || '';
  }

  /**
   * The value of the underlying multi-select field
   */
  get multiSelectValue(): string[] {
    if (_isNil(this._values) || !this._values?.length) return [];

    return this.allSelectOptions
      .filter(
        selectOption =>
          !!(this._values as SupportedItemType[]).find(value =>
            _isEqual(selectOption.formValue, value),
          ),
      )
      .map(_ => _?.displayValue?.value)
      .filter(Boolean);
  }

  /**
   * Takes the onChange value from a multiselect and gets the form value
   */
  public processMultiSelectChange(values: string[]): SupportedArrayTypes {
    if (!values) return [];

    return this.allSelectOptions
      .filter(_ => values.includes(_.displayValue.value))
      .map(_ => _.formValue) as SupportedArrayTypes;
  }

  public processAddOpenChoice(
    value: string | number,
  ): SupportedTypes | SupportedArrayTypes {
    if (!this._config.isMultiSelect) {
      return value;
    } else {
      const asSet = new Set([...(this._values || []), value]);
      return [...asSet] as SupportedArrayTypes;
    }
  }
}
